import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";
import { BookOpen, Loader2 } from "lucide-react";

interface EnrollStudentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentId: string;
  studentName: string;
  existingCourseIds?: string[];
}

export function EnrollStudentModal({
  open,
  onOpenChange,
  studentId,
  studentName,
  existingCourseIds = [],
}: EnrollStudentModalProps) {
  const queryClient = useQueryClient();
  const [enrollType, setEnrollType] = useState<"course" | "bundle">("course");
  const [selectedId, setSelectedId] = useState("");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  // Fetch available courses
  const { data: coursesData } = useQuery({
    queryKey: ["admin-courses-for-enroll"],
    queryFn: () => adminApi.getCourses(1, 100),
    enabled: open,
  });

  // Filter out courses the student is already enrolled in
  const availableCourses = coursesData?.items.filter(
    (course) => !existingCourseIds.includes(course.id),
  );

  const enrollMutation = useMutation({
    mutationFn: (data: {
      courseId?: string;
      bundleId?: string;
      expiresAt?: string;
      note?: string;
    }) => adminApi.enrollStudent(studentId, data),
    onSuccess: () => {
      toast.success("Estudiante inscrito exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["admin-student-detail-enhanced", studentId],
      });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al inscribir al estudiante");
    },
  });

  const handleClose = () => {
    setEnrollType("course");
    setSelectedId("");
    setNote("");
    setExpiresAt("");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    enrollMutation.mutate({
      courseId: enrollType === "course" ? selectedId : undefined,
      bundleId: enrollType === "bundle" ? selectedId : undefined,
      expiresAt: expiresAt || undefined,
      note: note.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Inscribir a Curso
          </DialogTitle>
          <DialogDescription>
            Inscribe manualmente a {studentName} en un curso o paquete.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de inscripcion</Label>
            <Select
              value={enrollType}
              onValueChange={(v) => {
                setEnrollType(v as "course" | "bundle");
                setSelectedId("");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="course">Curso individual</SelectItem>
                <SelectItem value="bundle">Paquete de cursos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {enrollType === "course" && (
            <div className="space-y-2">
              <Label>Seleccionar curso</Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un curso" />
                </SelectTrigger>
                <SelectContent>
                  {availableCourses?.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No hay cursos disponibles
                    </SelectItem>
                  ) : (
                    availableCourses?.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {availableCourses?.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  El estudiante ya esta inscrito en todos los cursos
                  disponibles.
                </p>
              )}
            </div>
          )}

          {enrollType === "bundle" && (
            <div className="space-y-2">
              <Label>Seleccionar paquete</Label>
              <p className="text-sm text-muted-foreground">
                La inscripcion a paquetes estara disponible proximamente.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="expiresAt">Fecha de expiracion (opcional)</Label>
            <Input
              id="expiresAt"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
            <p className="text-xs text-muted-foreground">
              Deja en blanco para acceso permanente.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Nota interna (opcional)</Label>
            <textarea
              id="note"
              className="w-full min-h-[60px] px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota sobre esta inscripcion..."
              maxLength={500}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                enrollMutation.isPending ||
                !selectedId ||
                enrollType === "bundle"
              }
            >
              {enrollMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Inscribiendo...
                </>
              ) : (
                <>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Inscribir
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
